import { type ReactNode } from 'react';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'default' }: { label: string; value: ReactNode; hint?: string; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const toneClass =
    tone === 'good' ? 'text-seagreen-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

// One entry per status/state/outcome string the M11/M12/M14 APIs actually
// return (see src/lib/types.ts) — unmapped statuses still render, just in
// the neutral slate tone.
const STATUS_TONES: Record<string, string> = {
  // Generic good / progressing / bad / neutral
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  VERIFIED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  ISSUED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CLEARED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PASS: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  MET: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FINAL: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  AWARDED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CONTRACTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  OPERATIONAL: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  OVERTURNED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  GREEN: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  DRAFT: 'bg-slate-100 text-slate-600 ring-slate-200',
  PENDING: 'bg-sky-50 text-sky-700 ring-sky-200',
  PENDING_DISPATCH: 'bg-amber-50 text-amber-700 ring-amber-200',
  PENDING_REFERRAL: 'bg-amber-50 text-amber-700 ring-amber-200',
  REFERRAL_PENDING: 'bg-amber-50 text-amber-700 ring-amber-200',
  REFERRED: 'bg-sky-50 text-sky-700 ring-sky-200',
  SUBMITTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  APPLIED: 'bg-sky-50 text-sky-700 ring-sky-200',
  OPEN: 'bg-sky-50 text-sky-700 ring-sky-200',
  PUBLISHED: 'bg-sky-50 text-sky-700 ring-sky-200',
  VALIDATED: 'bg-sky-50 text-sky-700 ring-sky-200',
  UNDER_REVIEW: 'bg-sky-50 text-sky-700 ring-sky-200',
  REQUIREMENTS_GATHERING: 'bg-sky-50 text-sky-700 ring-sky-200',
  PROPOSED: 'bg-sky-50 text-sky-700 ring-sky-200',

  CLARIFICATIONS: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  UNSEALED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  CONFIRMED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  ALL_MET: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  DECIDED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  UPHELD: 'bg-indigo-50 text-indigo-700 ring-indigo-200',

  EVALUATION: 'bg-violet-50 text-violet-700 ring-violet-200',
  IN_DELIVERY: 'bg-violet-50 text-violet-700 ring-violet-200',
  IN_PROGRESS: 'bg-violet-50 text-violet-700 ring-violet-200',

  FEE_PENDING: 'bg-amber-50 text-amber-700 ring-amber-200',
  AWARD_PROPOSED: 'bg-amber-50 text-amber-700 ring-amber-200',
  STANDSTILL: 'bg-amber-50 text-amber-700 ring-amber-200',
  BOARD_AGENDA: 'bg-amber-50 text-amber-700 ring-amber-200',
  SHOW_CAUSE_ISSUED: 'bg-amber-50 text-amber-700 ring-amber-200',
  APPEALED: 'bg-amber-50 text-amber-700 ring-amber-200',
  WAIVED: 'bg-amber-50 text-amber-700 ring-amber-200',
  SUSPEND: 'bg-amber-50 text-amber-700 ring-amber-200',
  DEFERRED: 'bg-amber-50 text-amber-700 ring-amber-200',
  SUSPENDED: 'bg-amber-50 text-amber-700 ring-amber-200',
  AMBER: 'bg-amber-50 text-amber-700 ring-amber-200',
  WARN: 'bg-amber-50 text-amber-700 ring-amber-200',

  REJECTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  DENIED: 'bg-rose-50 text-rose-700 ring-rose-200',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-200',
  FAIL: 'bg-rose-50 text-rose-700 ring-rose-200',
  REVOKED: 'bg-rose-50 text-rose-700 ring-rose-200',
  REVOKE: 'bg-rose-50 text-rose-700 ring-rose-200',
  REVERSED: 'bg-rose-50 text-rose-700 ring-rose-200',
  BLACKLISTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  MISSED: 'bg-rose-50 text-rose-700 ring-rose-200',
  RED: 'bg-rose-50 text-rose-700 ring-rose-200',

  WITHDRAWN: 'bg-slate-100 text-slate-500 ring-slate-200',
  CANCELLED: 'bg-slate-100 text-slate-500 ring-slate-200',
  CLOSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  DISMISSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  DISMISS: 'bg-slate-100 text-slate-500 ring-slate-200',
  EXPIRED: 'bg-slate-100 text-slate-500 ring-slate-200',
  NONE: 'bg-slate-100 text-slate-500 ring-slate-200',
  UNAVAILABLE: 'bg-slate-100 text-slate-500 ring-slate-200',
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}>
      {status.replaceAll('_', ' ')}
    </span>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: ReactNode }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{message}</div>
  );
}

export function InfoNote({ message }: { message: ReactNode }) {
  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50 px-3.5 py-2.5 text-sm text-sky-800">{message}</div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-sm font-semibold text-slate-600">{title}</div>
      {hint ? <div className="mt-1 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={onClose}>
      <div
        className={`card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} p-5`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, className = '', hint }: { label: string; children: ReactNode; className?: string; hint?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="field-label">{label}</span>
      {children}
      {hint ? <span className="mt-0.5 block text-[11px] font-normal normal-case tracking-normal text-slate-400">{hint}</span> : null}
    </label>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: ReadonlyArray<{ key: T; label: string }>; value: T; onChange: (t: T) => void }) {
  return (
    <div className="mb-4 flex flex-wrap gap-1 rounded-xl bg-slate-200/60 p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`flex-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
            value === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
